import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Modal } from '@components/Modal';
import { useTwinData } from '@hooks/storeHooks/useTwin';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { useDeleteTwin } from '../../../../hooks/apiHooks/twinHooks';

interface IRemoveTwinProps {
  refetchUser: () => void;
}

const RemoveTwin: React.FC<IRemoveTwinProps> = memo(({ refetchUser }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync, isPending } = useDeleteTwin();
  const { setLocalTwinIsReady, resetTwinState } = useTwinData();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const removeTwin = async (): Promise<void> => {
    try {
      const res = await mutateAsync();
      if (res.success) {
        setIsOpen(false);
        toast.success(t('twin.twinRemoved'));
        setLocalTwinIsReady(false);
        resetTwinState();
        refetchUser();
      }
    } catch (error) {
      console.error('Error removing twin:', error);
      toast.error(t('twin.twinRemoveFailed'));
    }
  };

  return (
    <div className="mt-14">
      <button
        data-testid="remove-twin-open-btn"
        onClick={handleOpen}
        className="underline block mx-auto">
        {t('twin.deletePrompt')}
      </button>

      <Modal title={t('twin.removeTwin')} onClose={handleClose} isOpen={isOpen}>
        <>
          <p className="text-white pb-6">
            {t('twin.removeConfirmation')}
          </p>
          <div className="flex justify-end gap-4 border-t border-white/10 pt-6">
            <button
              data-testid="remove-twin-cancel-btn"
              onClick={handleClose}
              className="btn btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              data-testid="remove-twin-confirm-btn"
              onClick={removeTwin}
              disabled={isPending}
              className="btn bg-red-600 text-white rounded-md hover:bg-red-700">
              {isPending && <AnimatedSpinner className="mr-2" />}
              {t('account.remove')}
            </button>
          </div>
        </>
      </Modal>
    </div>
  );
});

export { RemoveTwin };
